import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, FileText, Loader2 } from 'lucide-react';

const sellerSchema = z.object({
  company_name: z.string().trim().min(2, 'Company name must be at least 2 characters').max(200, 'Company name too long'),
  contact_name: z.string().trim().min(2, 'Contact name must be at least 2 characters').max(100, 'Contact name too long'),
  email: z.string().trim().email('Invalid email address').max(255, 'Email too long'),
  phone: z.string().trim().max(20, 'Phone number too long').optional(),
  portfolio_description: z.string().trim().max(2000, 'Description too long').optional(),
});

type SellerFormData = z.infer<typeof sellerSchema>;

export const SellerRegistration = () => {
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SellerFormData>({
    resolver: zodResolver(sellerSchema),
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File size must be less than 20MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'application/pdf',
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a CSV, Excel, or PDF file');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const onSubmit = async (data: SellerFormData) => {
    setUploading(true);
    
    try {
      let filePath = null;
      let fileName = null;
      let fileSize = null;

      // Upload file if selected
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop();
        const timestamp = Date.now();
        filePath = `${timestamp}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('seller-uploads')
          .upload(filePath, selectedFile);

        if (uploadError) {
          throw uploadError;
        }

        fileName = selectedFile.name;
        fileSize = selectedFile.size;
      }

      // Insert inquiry record
      const { error: insertError } = await supabase
        .from('seller_inquiries')
        .insert({
          company_name: data.company_name,
          contact_name: data.contact_name,
          email: data.email,
          phone: data.phone || null,
          portfolio_description: data.portfolio_description || null,
          file_path: filePath,
          file_name: fileName,
          file_size: fileSize,
          status: 'pending',
        });

      if (insertError) {
        throw insertError;
      }

      toast.success('Thank you! We\'ll review your portfolio and contact you soon.');
      reset();
      setSelectedFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('portfolio-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error submitting seller inquiry:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section id="seller-registration" className="py-20 bg-background">
      <div className="container px-4 mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Sell Your Debt Portfolio
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have a debt portfolio to sell? Register below and upload your book. 
            We'll review it and connect you with qualified buyers on our marketplace.
          </p>
        </div>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle>Seller Registration</CardTitle>
            <CardDescription>
              Fill in your details and upload your portfolio file
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="company_name">
                    Company Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="company_name"
                    placeholder="ABC Collections Ltd"
                    {...register('company_name')}
                    disabled={uploading}
                  />
                  {errors.company_name && (
                    <p className="text-sm text-destructive">{errors.company_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_name">
                    Contact Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="contact_name"
                    placeholder="John Smith"
                    {...register('contact_name')}
                    disabled={uploading}
                  />
                  {errors.contact_name && (
                    <p className="text-sm text-destructive">{errors.contact_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@company.com"
                    {...register('email')}
                    disabled={uploading}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 234 567 8900"
                    {...register('phone')}
                    disabled={uploading}
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive">{errors.phone.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio_description">
                  Portfolio Description (optional)
                </Label>
                <Textarea
                  id="portfolio_description"
                  placeholder="Brief description of your debt portfolio (type, size, geography, etc.)"
                  rows={4}
                  {...register('portfolio_description')}
                  disabled={uploading}
                />
                {errors.portfolio_description && (
                  <p className="text-sm text-destructive">{errors.portfolio_description.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio-file">
                  Upload Portfolio File (CSV, Excel, or PDF)
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="portfolio-file"
                    type="file"
                    accept=".csv,.xlsx,.xls,.pdf"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    className="cursor-pointer"
                  />
                  {selectedFile && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Maximum file size: 20MB. Supported formats: CSV, Excel, PDF
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Submit Portfolio
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
